import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import UserInfoProvider, { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import TrackerProvider from './TrackerContext';
import { getActionModalOrPanel, getActionOnConfirm, mergeActionOverrides } from '../utils/actions';
import { useTrackAssetActionInteraction } from '../hooks/useTrackAssetActionInteraction';
import { getDefaultActions } from '../constants/defaultActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AssetActionsContext = /*#__PURE__*/createContext(undefined);
AssetActionsContext.displayName = 'AssetActionsContext';
function AssetActionsProviderInner({
  actions,
  actionsConfigOverrides,
  children,
  crmObject,
  layoutUse = 'detail-actions',
  nonCrmObject,
  onActionError,
  onActionSuccess
}) {
  const [showModal, setShowModal] = useState(false);
  const [clickedActionId, setClickedActionId] = useState('');
  const actionsWithOverrides = useMemo(() => {
    return mergeActionOverrides({
      actionsConfig: actions || getDefaultActions(),
      actionsConfigOverrides
    });
  }, [actions, actionsConfigOverrides]);
  const {
    scopes,
    gates
  } = useUserInfo();
  const trackAssetActionInteraction = useTrackAssetActionInteraction();
  const handleActionClick = useCallback(id => {
    setClickedActionId(id);
    trackAssetActionInteraction({
      action: 'click-action',
      actionType: id,
      crmObject,
      nonCrmObject
    });
    /**
     * When ModalOrPanel component is defined, we defer calls to both success and confirm actions
     * to the modal.
     */
    if (getActionModalOrPanel(actionsWithOverrides, id)) {
      setShowModal(true);
      return;
    }
    const onActionConfirm = getActionOnConfirm(actionsWithOverrides, id);
    if (onActionConfirm) {
      onActionConfirm(crmObject);
    }
    if (onActionSuccess) {
      onActionSuccess({
        actionId: id,
        data: crmObject
      });
    }
  }, [trackAssetActionInteraction, crmObject, nonCrmObject, actionsWithOverrides, onActionSuccess]);
  const ModalOrPanel = getActionModalOrPanel(actionsWithOverrides, clickedActionId);
  const onActionConfirm = getActionOnConfirm(actionsWithOverrides, clickedActionId);
  const handleModalOrPanelConfirm = useCallback((_actionId, result) => {
    if (onActionConfirm) {
      onActionConfirm(result || crmObject);
    }
    if (onActionSuccess) {
      onActionSuccess({
        actionId: _actionId,
        data: result
      });
    }
    setShowModal(false);
  }, [crmObject, onActionConfirm, onActionSuccess, setShowModal]);
  const value = useMemo(() => ({
    actions,
    actionsWithOverrides,
    actionsConfigOverrides,
    crmObject,
    layoutUse,
    nonCrmObject,
    onActionError,
    onActionSuccess,
    handleActionClick,
    scopes,
    gates
  }), [actions, actionsWithOverrides, actionsConfigOverrides, crmObject, layoutUse, nonCrmObject, onActionError, onActionSuccess, handleActionClick, scopes, gates]);
  return /*#__PURE__*/_jsxs(AssetActionsContext.Provider, {
    value: value,
    children: [children, showModal && ModalOrPanel && /*#__PURE__*/_jsx(ModalOrPanel, {
      crmObject: crmObject,
      gates: gates,
      nonCrmObject: nonCrmObject,
      onClose: () => setShowModal(false),
      onConfirm: handleModalOrPanelConfirm,
      scopes: scopes
    })]
  });
}
export default function AssetActionsProvider({
  actions,
  actionsConfigOverrides,
  children,
  crmObject,
  layoutUse,
  nonCrmObject,
  onActionError,
  onActionSuccess,
  trackerProps
}) {
  const objectId = useMemo(() => {
    if (!crmObject || !crmObject.objectId) {
      return 0;
    }
    return parseInt(getPropertyValue(crmObject, 'hs_object_id') || '0', 10);
  }, [crmObject]);
  const objectTypeId = useMemo(() => {
    if (!crmObject || !crmObject.objectTypeId) {
      return '0';
    }
    return crmObject.objectTypeId;
  }, [crmObject]);
  return /*#__PURE__*/_jsx(TrackerProvider, Object.assign({
    objectTypeId: objectTypeId,
    objectId: objectId
  }, trackerProps, {
    children: /*#__PURE__*/_jsx(UserInfoProvider, {
      children: /*#__PURE__*/_jsx(AssetActionsProviderInner, {
        actions: actions,
        actionsConfigOverrides: actionsConfigOverrides,
        crmObject: crmObject,
        layoutUse: layoutUse,
        nonCrmObject: nonCrmObject,
        onActionError: onActionError,
        onActionSuccess: onActionSuccess,
        trackerProps: trackerProps,
        children: children
      })
    })
  }));
}

/**
 * Custom hook to access the AssetActionsContext.
 *
 * This hook provides access to the context values defined in AssetActionsProvider.
 * It must be used within a component that is a descendant of AssetActionsProvider.
 *
 * @template NonCrmObject - The type of the non-CRM object.
 * @returns {AssetActionsContextProps<NonCrmObject>} The context values.
 * @throws {Error} If used outside of an AssetActionsProvider.
 */
export function useAssetActions() {
  const context = useContext(AssetActionsContext);
  if (context === undefined) {
    throw new Error('useAssetActions must be used within a AssetActionsProvider');
  }
  return context;
}